import { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { foilFragment, foilVertex } from './foilShader';
import { audio } from '../../audio/engine';

interface Props {
  position: [number, number, number];
  rotation: [number, number, number];
  selected: boolean;
  texture: THREE.Texture;
  foil?: boolean;
  reducedMotion: boolean;
  dimmed?: boolean;
  onClick: () => void;
  onDoubleClick: () => void;
  onDragPlay?: () => void;
}

export function FoilCard({
  position,
  rotation,
  selected,
  texture,
  foil,
  reducedMotion,
  dimmed,
  onClick,
  onDoubleClick,
  onDragPlay,
}: Props) {
  const mesh = useRef<THREE.Mesh>(null);
  const dragStartY = useRef<number | null>(null);

  const material = useMemo(() => {
    return new THREE.ShaderMaterial({
      uniforms: {
        uArt: { value: texture },
        uTime: { value: 0 },
        uHover: { value: 0 },
        uSelected: { value: selected ? 1 : 0 },
        uDim: { value: dimmed ? 0.45 : 1 },
        uFoil: { value: foil ? 1 : 0 },
      },
      transparent: true,
      vertexShader: foilVertex,
      fragmentShader: foilFragment,
    });
  }, [texture, selected, dimmed, foil]);

  useFrame((state) => {
    if (!mesh.current) return;
    const mat = mesh.current.material as THREE.ShaderMaterial;
    mat.uniforms.uTime.value = state.clock.elapsedTime;
    if (!reducedMotion) {
      const tiltX = state.pointer.y * 0.15;
      const tiltY = state.pointer.x * 0.15;
      mesh.current.rotation.x = rotation[0] + tiltX;
      mesh.current.rotation.y = rotation[1] + tiltY;
      mesh.current.rotation.z = rotation[2];
    }
    mat.uniforms.uSelected.value = selected ? 1 : 0;
  });

  return (
    <mesh
      ref={mesh}
      position={position}
      rotation={rotation}
      onPointerDown={(e) => {
        dragStartY.current = e.clientY;
      }}
      onPointerUp={(e) => {
        if (dragStartY.current === null) return;
        const dy = dragStartY.current - e.clientY;
        dragStartY.current = null;
        if (dy > 48 && onDragPlay) {
          e.stopPropagation();
          onDragPlay();
          return;
        }
      }}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      onDoubleClick={(e) => {
        e.stopPropagation();
        onDoubleClick();
      }}
      onPointerOver={() => {
        (mesh.current?.material as THREE.ShaderMaterial).uniforms.uHover.value = 1;
        audio.playSfx('card_hover', { volume: 0.55, minGapMs: 120 });
      }}
      onPointerOut={() => {
        (mesh.current?.material as THREE.ShaderMaterial).uniforms.uHover.value = 0;
      }}
    >
      <planeGeometry args={[0.85, 1.2]} />
      <primitive object={material} attach="material" />
    </mesh>
  );
}
